import React, { FC, ReactNode, useEffect, useState } from "react";

import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Box, { BoxProps } from "@mui/material/Box";
import Typography, { TypographyProps } from "@mui/material/Typography";

import Divider from "components/Divider";

import styles from "./styles";

interface CustomAccordionProps {
  initialOpen?: boolean;
  title: string | ReactNode;
  children:
    | JSX.Element
    | JSX.Element[]
    | string
    | (string | number | JSX.Element)[]
    | null;
  disableGutters?: boolean;
  isDivider?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  boxProps?: Omit<BoxProps, "ref">;
  titleTypographyProps?: Omit<TypographyProps, "ref">;
}

const CustomAccordion: FC<CustomAccordionProps> = ({
  initialOpen = false,
  title,
  children,
  disableGutters = false,
  isDivider = true,
  onOpen,
  onClose,
  boxProps = {},
  titleTypographyProps = {},
}) => {
  const [expanded, setExpanded] = useState(initialOpen);

  useEffect(() => {
    setExpanded(initialOpen);
  }, [initialOpen]);

  const handleExpanded = (panel) => (_, isExpanded) => {
    const isOpen = isExpanded ? panel : false;
    isOpen && onOpen && onOpen();
    !isOpen && onClose && onClose();
    setExpanded(isOpen);
  };

  return (
    <Box mb={3} {...boxProps}>
      <Accordion
        expanded={expanded}
        sx={styles.accordion}
        onChange={handleExpanded(!expanded)}
      >
        <AccordionSummary
          expandIcon={
            <KeyboardArrowDownIcon
              color="primary"
              sx={{
                fontSize: "2rem",
              }}
            />
          }
          sx={disableGutters ? { p: 0 } : {}}
        >
          {typeof title === "string" ? (
            <Typography component="h3" variant="h4" {...titleTypographyProps}>
              {title}
            </Typography>
          ) : (
            title
          )}
        </AccordionSummary>
        <AccordionDetails sx={disableGutters ? { p: 0 } : {}}>
          <Box width="100%">{children}</Box>
        </AccordionDetails>
      </Accordion>
      {isDivider && <Divider height={2} />}
    </Box>
  );
};

export default CustomAccordion;
